import { PageContainer, PageHeader, VStack } from '@/components/Atoms'
import { Card } from '@/components/Card'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import { HandCoins, TargetIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CurrencyRatesDialog } from './-components/CurrencyRatesDialog'
import { LmcTargetsDialog } from './-components/LmcTargetsDialog'
import { SETTINGS_ANCHORS, type SettingsAnchor } from './-components/settings-anchors'
import { SettingsTile } from './-components/SettingsTile'

export const Route = createFileRoute('/_auth/_modules/crt/settings/')({
  component: RouteComponent,
})

/**
 * CRT settings that are app-wide rather than owned by one page: the currency rates the header's
 * display-currency picker offers, and the full-year (LMC) targets the reports compare against. Each
 * is a tile that opens its dialog, and shows only to users with its permission; the route needs at
 * least one of them.
 *
 * The open dialog is the URL's hash, so a link to `/crt/settings#lmc-targets` (e.g. the data issues'
 * "Set targets") lands with that dialog open.
 */
function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const { hash } = useLocation()
  const navigate = useNavigate()

  const dialogProps = (anchor: SettingsAnchor) => ({
    isOpen: hash === anchor,
    // Replace rather than push, so Back leaves the page instead of reopening the dialog.
    onOpenChange: (isOpen: boolean) => {
      if (!isOpen) void navigate({ to: '.', hash: undefined, replace: true })
    },
  })

  return (
    <PageContainer>
      {/* The title shares the tiles' centered column so its edge lines up with theirs. */}
      <VStack gap="lg" align="center">
        <div className="w-full max-w-2xl">
          <PageHeader>{t('route.crt.settings', { ns: 'common' })}</PageHeader>
        </div>
        <Card className="divide-border w-full max-w-2xl divide-y">
          <PermissionsGuard required={['crt_currency_rates_manage']}>
            <SettingsTile
              anchor={SETTINGS_ANCHORS.currencyRates}
              icon={HandCoins}
              title={t('currencyRates.title')}
              description={t('crtSettings.currencyRates.tileDescription')}
            />
            <CurrencyRatesDialog {...dialogProps(SETTINGS_ANCHORS.currencyRates)} />
          </PermissionsGuard>
          <PermissionsGuard required={['crt_lmc_targets_view']}>
            <SettingsTile
              anchor={SETTINGS_ANCHORS.lmcTargets}
              icon={TargetIcon}
              title={t('kpiReports.lmcTargets.title')}
              description={t('crtSettings.lmcTargets.tileDescription')}
            />
            <LmcTargetsDialog {...dialogProps(SETTINGS_ANCHORS.lmcTargets)} />
          </PermissionsGuard>
        </Card>
      </VStack>
    </PageContainer>
  )
}
